import cx from 'classnames';
import { type FC, useEffect, useMemo, useState } from 'react';
import { type RouteComponentProps, useLocation } from 'wouter';

import {
  type PaymentMethod,
  PaymentMethodType,
  paymentMethodsRequest,
} from 'api/payments';
import AccountPageNavigation from 'components/AccountPageNavigation';
import ErrorEmptyState from 'components/EmptyStates/ErrorEmptyState';
import Icon from 'components/Icon/Icon';
import AccountLayout from 'components/Layouts/Account';
import Loader from 'components/Loader';
import Meta from 'components/Meta';
import PaymentForm from 'components/PaymentForm';
import PaymentItem from 'components/PaymentItem';
import UserInfo from 'components/UserInfo';
import { useClientProfile } from 'store';
import { getMediaUrl } from 'utils/assets';
import useLayout, { LayoutType } from 'utils/layout';
import { Translation } from 'utils/translations';
import { LoadingState } from 'utils/types';
import useLazyRequest from 'utils/useLazyRequest';

import styles from './PaymentsPage.module.scss';

type Page = {
  title: string;
  link: string;
};

const PageTabs: Page[] = [
  {
    title: 'deposit',
    link: '/payment/deposit',
  },
  {
    title: 'withdraw',
    link: '/payment/withdrawal',
  },
];

const pageTitles = {
  deposit: 'title_deposit',
  withdrawal: 'title_withdrawal',
  default: 'title_payments',
};

const emptyPaymentMethods: Array<PaymentMethod> = [];

export type PaymentPageParams = {
  method?: PaymentMethodType;
  activeMethodId?: string;
};

const PaymentsPage: FC<RouteComponentProps<PaymentPageParams>> = ({
  params: { method, activeMethodId },
}) => {
  const layout = useLayout();
  const [{ sessionToken }] = useClientProfile();
  const paymentMethod = method?.toUpperCase() as PaymentMethodType;

  const desktopPageView = layout === LayoutType.wide;
  const isMobile = layout === LayoutType.mobile;
  const [, setLocation] = useLocation();
  const {
    data: response,
    loading,
    execute,
  } = useLazyRequest(paymentMethodsRequest);

  useEffect(() => {
    if (method) {
      execute({
        body: {
          operationType: method.toString().toUpperCase(),
          deviceType: layout === LayoutType.wide ? 'Desktop' : 'Mobile',
        },
        headers: {
          authorization: `Bearer ${sessionToken}`,
        },
      });
    }
  }, [method, sessionToken, execute, layout]);

  const [selectedPayment, setSelectedPayment] = useState<PaymentMethod | null>(
    null,
  );
  const paymentMethods = response?.data || emptyPaymentMethods;
  const isPaymentsEmpty = paymentMethods.length === 0;
  const isLoaded = loading === LoadingState.success;
  const isOnlyOnePaymentMethod = paymentMethods.length === 1;

  useEffect(() => {
    if (!method) {
      setLocation('/payment/deposit', { replace: true });
      return;
    }
    if (activeMethodId || paymentMethods.length === 0) return;

    setLocation(`/payment/${method}/${paymentMethods[0].id}`, {
      replace: true,
    });
  }, [activeMethodId, method, setLocation, paymentMethods]);

  useEffect(() => {
    if (!activeMethodId) return;
    const payment = paymentMethods.find(
      ({ id }) => id === Number(activeMethodId),
    );
    if (payment) {
      setSelectedPayment(payment);
    }
  }, [activeMethodId, paymentMethods]);

  const title = useMemo(
    () => (
      <Translation
        message={
          pageTitles[
            (method?.toLowerCase() as keyof typeof pageTitles) || 'default'
          ]
        }
      />
    ),
    [method],
  );

  return (
    <AccountLayout
      className={cx(styles.page, styles[layout])}
      title="deposit_and_withdraw"
      icon="profile-circle"
    >
      <Meta title={title} />
      <div className={styles.content}>
        <div className={styles['page-header']}>
          <AccountPageNavigation
            className={cx(styles.links, {
              [styles['full-width']]: !desktopPageView,
            })}
            pages={PageTabs}
          />
          {desktopPageView && isLoaded && !isPaymentsEmpty && (
            <UserInfo className={styles['user-info']} />
          )}
        </div>

        {!isPaymentsEmpty && isLoaded && method && (
          <>
            <div className={styles['page-title']}>
              <Icon name="payment-methods" />
              <Translation
                message={
                  paymentMethod === PaymentMethodType.DEPOSIT
                    ? 'payment_methods'
                    : 'withdrawal_methods'
                }
              />
            </div>

            <div className={styles['payments-list']}>
              {paymentMethods.map(
                ({ desktopImageLink, id, maxAmount, minAmount, name }) => (
                  <PaymentItem
                    key={id}
                    id={id}
                    name={name}
                    className={cx({
                      [styles['single-item']]: isOnlyOnePaymentMethod,
                    })}
                    imageClassName={styles.image}
                    renderInfo={!isMobile || !isOnlyOnePaymentMethod}
                    icon={
                      !isOnlyOnePaymentMethod && (
                        <Icon
                          className={styles.icon}
                          name="ic_round-check"
                          height={32}
                          width={32}
                        />
                      )
                    }
                    image={desktopImageLink}
                    minAmount={minAmount}
                    maxAmount={maxAmount}
                    method={method}
                  />
                ),
              )}
            </div>
          </>
        )}
      </div>

      {loading === LoadingState.loading && (
        <div className={styles.loader}>
          <Loader />
        </div>
      )}

      {isPaymentsEmpty && loading === LoadingState.failed && (
        <div className={styles['payments-page-error']}>
          <ErrorEmptyState />
        </div>
      )}

      {isPaymentsEmpty && isLoaded && (
        <div className={styles['empty-payments']}>
          <img
            width={140}
            height={140}
            alt="empty"
            src={getMediaUrl('/yellow/empty-documents.png')}
          />
          <span className={styles['empty-text']}>
            <Translation message="no_payment_methods" />
          </span>
        </div>
      )}
      {selectedPayment && isLoaded && paymentMethod && (
        <PaymentForm method={paymentMethod} selectedPayment={selectedPayment} />
      )}
    </AccountLayout>
  );
};

export default PaymentsPage;
